/**
 * Helpers that keep grid exports (Excel / CSV / PDF) in sync with whatever the
 * user has typed into a DataTable's search box.
 *
 * Without these, an export always dumps the full data set even though the grid
 * on screen is showing a filtered subset - which is not what the user expects
 * when they hit "Download" right after searching.
 */

const resolveInstance = (dataTable) => {
  const dt = dataTable && dataTable.current !== undefined ? dataTable.current : dataTable;
  return dt && typeof dt.rows === "function" ? dt : null;
};

/**
 * Global search term currently applied to the table ("" when none).
 * Accepts either a DataTables API instance or the ref holding one.
 */
export const getDataTableSearchTerm = (dataTable) => {
  const dt = resolveInstance(dataTable);
  if (!dt) return "";
  try {
    return String(dt.search() || "").trim();
  } catch (err) {
    return "";
  }
};

/**
 * True when the grid is showing a subset of its data because of the global
 * search box or any per-column search.
 */
export const isDataTableFiltered = (dataTable) => {
  const dt = resolveInstance(dataTable);
  if (!dt) return false;
  try {
    if (String(dt.search() || "").trim()) return true;
    return dt
      .columns()
      .search()
      .toArray()
      .some((term) => String(term || "").trim() !== "");
  } catch (err) {
    return false;
  }
};

/**
 * Rows an export should write out.
 *
 * When a search is active, returns only the matching rows, in the order the
 * grid is currently sorted by - across every page, not just the visible one.
 * With no search active (or when the rows can't be read safely) the caller's
 * own data set is returned unchanged, so existing behaviour is preserved.
 *
 * @param {object} dataTable  DataTables API instance, or a ref holding one
 * @param {Array}  fallbackRows  the page's full data set
 */
export const getDataTableExportRows = (dataTable, fallbackRows = []) => {
  const allRows = Array.isArray(fallbackRows) ? fallbackRows : [];
  const dt = resolveInstance(dataTable);
  if (!dt) return allRows;

  try {
    if (!isDataTableFiltered(dt)) return allRows;

    const matched = dt.rows({ search: "applied", order: "applied" });
    const filtered = matched.data().toArray();

    // Tables initialised from existing DOM markup hand back arrays of cell HTML
    // instead of the source objects; exporting those would break the caller's
    // field mapping. Those rows still carry their original index though, so map
    // the matches back onto the caller's data set instead.
    const usesSourceObjects = filtered.every(
      (row) => row && typeof row === "object" && !Array.isArray(row)
    );
    if (!usesSourceObjects) {
      if (dt.rows().count() !== allRows.length) return allRows;
      return matched
        .indexes()
        .toArray()
        .map((i) => allRows[i])
        .filter((row) => row !== undefined);
    }

    return filtered;
  } catch (err) {
    console.warn("Unable to read filtered DataTable rows; exporting full data set", err);
    return allRows;
  }
};

/**
 * Suffix for an export filename so a filtered download is distinguishable from
 * a full one, e.g. `Country_Master_2026-07-28_filtered.xlsx`.
 */
export const getFilteredFileTag = (dataTable) =>
  isDataTableFiltered(dataTable) ? "_filtered" : "";
